/* eslint-disable react/no-unescaped-entities */
"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { BrandMark } from "@/components/page/auth-shell";
import { isSeaaiiHost } from "@/lib/brand";
import { applySeaaiiRebrand } from "@/lib/seaaiiRebrand";

type Tab = "terms" | "privacy";
type Lang = "th" | "en";

// ─── UI chrome strings (header, tabs, footer) ───────────────
const UI = {
  th: {
    pageTitle: "ข้อกำหนดและความเป็นส่วนตัว",
    pageSubtitle:
      "อ่านเงื่อนไขการใช้งานและนโยบายความเป็นส่วนตัวของ FlowCloser เพื่อให้คุณเข้าใจสิทธิและความรับผิดชอบของทั้งสองฝ่าย",
    tabTerms: "เงื่อนไขการใช้งาน",
    tabPrivacy: "นโยบายความเป็นส่วนตัว",
    backTopLink: "← กลับหน้าหลัก",
    backBottomLink: "กลับสู่หน้าหลัก",
    effective: "มีผลบังคับใช้:",
    effectiveDate: "4 พฤษภาคม 2569",
    safeNote: "ข้อมูลของคุณปลอดภัยกับเรา",
    fontFamily: "'Noto Sans Thai', system-ui, sans-serif",
  },
  en: {
    pageTitle: "Terms & Privacy",
    pageSubtitle:
      "Review FlowCloser's Terms of Service and Privacy Policy to understand the rights and responsibilities of both parties.",
    tabTerms: "Terms of Service",
    tabPrivacy: "Privacy Policy",
    backTopLink: "← Back to home",
    backBottomLink: "Back to home",
    effective: "Effective:",
    effectiveDate: "May 4, 2026",
    safeNote: "Your data is safe with us",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
} as const;

export default function TermsAndPrivacyPage() {
  const [activeTab, setActiveTab] = useState<Tab>("terms");
  const [lang, setLang] = useState<Lang>("th");
  const t = UI[lang];

  // Host detection is client-only, so the header logo (and text rebrand
  // below) resolve in an effect. Brief FlowCloser flash on first paint is
  // acceptable — same trade-off the text rebrand already makes.
  const [isSeaaii, setIsSeaaii] = useState(false);
  useEffect(() => {
    setIsSeaaii(isSeaaiiHost(window.location.host));
  }, []);

  // Seaaii host → rebrand the rendered legal text + mailto links in place.
  // Re-runs on tab/lang change because React re-renders fresh FlowCloser text.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSeaaiiHost(window.location.host) && rootRef.current) {
      applySeaaiiRebrand(rootRef.current);
    }
  }, [activeTab, lang]);

  return (
    <div
      ref={rootRef}
      className="min-h-screen"
      style={{
        background: "var(--bg-soft)",
        color: "var(--ink)",
        fontFamily: t.fontFamily,
      }}
    >
      {/* Navy header band */}
      <header
        className="relative overflow-hidden"
        style={{
          color: "#fff",
          background:
            "radial-gradient(900px 400px at 80% -10%, rgba(96,165,250,.45), transparent 60%)," +
            "linear-gradient(135deg, var(--blue-950) 0%, var(--blue-900) 50%, var(--blue-700) 100%)",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.18,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 40%,#000 30%,transparent 80%)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 40%,#000 30%,transparent 80%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 pt-10 pb-14">
          <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
            <Link href="/" className="inline-flex items-center gap-3">
              {isSeaaii ? (
                // White-wordmark Seaaii logo (bird stays colourful, "Seaaii"
                // is white) — legible on the navy header.
                <Image
                  src="/seaaii/assets/img/logo-footer.png"
                  alt="Seaaii"
                  width={276}
                  height={100}
                  priority
                  className="h-9 w-auto"
                  style={{ objectFit: "contain" }}
                />
              ) : (
                <>
                  <BrandMark size={28} />
                  <span className="font-bold text-[18px] tracking-tight">
                    <span style={{ color: "#fff" }}>Flow</span>
                    <span
                      className="font-semibold"
                      style={{
                        background: "linear-gradient(90deg, #bfdbfe, #fff)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                      }}
                    >
                      Closer
                    </span>
                  </span>
                </>
              )}
            </Link>

            {/* Language toggle */}
            <div
              className="inline-flex rounded-full p-1 text-xs font-semibold"
              style={{
                background: "rgba(255,255,255,.08)",
                border: "1px solid rgba(255,255,255,.16)",
              }}
            >
              <LangPill active={lang === "th"} onClick={() => setLang("th")}>
                ไทย
              </LangPill>
              <LangPill active={lang === "en"} onClick={() => setLang("en")}>
                EN
              </LangPill>
            </div>
          </div>

          <h1
            className="text-[36px] sm:text-[44px] font-bold tracking-tight leading-tight"
            style={{ textWrap: "balance" }}
          >
            {t.pageTitle}
          </h1>
          <p className="mt-3 text-[15px] max-w-2xl" style={{ color: "#cfdcf5" }}>
            {t.pageSubtitle}
          </p>
        </div>
      </header>

      {/* Sticky tab bar */}
      <div
        className="sticky top-0 z-20"
        style={{
          background: "rgba(255,255,255,.85)",
          backdropFilter: "saturate(160%) blur(12px)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 flex items-center gap-1">
          <TabButton active={activeTab === "terms"} onClick={() => setActiveTab("terms")}>
            {t.tabTerms}
          </TabButton>
          <TabButton active={activeTab === "privacy"} onClick={() => setActiveTab("privacy")}>
            {t.tabPrivacy}
          </TabButton>
          <div className="ml-auto py-3">
            <Link
              href="/"
              className="text-xs hover:underline"
              style={{ color: "var(--ink-3)" }}
            >
              {t.backTopLink}
            </Link>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 py-14">
        <article
          className="rounded-2xl"
          style={{
            background: "#fff",
            border: "1px solid var(--line)",
            boxShadow: "0 24px 60px -20px rgba(11,20,55,.12), 0 0 0 1px var(--line)",
            padding: "44px 40px",
          }}
        >
          {activeTab === "terms"
            ? lang === "th" ? <TermsContentTH /> : <TermsContentEN />
            : lang === "th" ? <PrivacyContentTH /> : <PrivacyContentEN />}

          <footer
            className="mt-10 pt-6 border-t flex items-center justify-between flex-wrap gap-3 text-xs"
            style={{ borderColor: "var(--line)", color: "var(--ink-3)" }}
          >
            <span>
              {t.effective}{" "}
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{t.effectiveDate}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" style={{ color: "var(--blue-700)" }}>
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              {t.safeNote}
            </span>
          </footer>
        </article>

        <div className="text-center mt-8">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            {t.backBottomLink}
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Reusable UI components ─────────────────────────────────
function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-3.5 text-[14px] font-semibold transition-colors relative"
      style={{
        color: active ? "var(--blue-700)" : "var(--ink-2)",
      }}
    >
      {children}
      {active && (
        <span
          className="absolute left-3 right-3 bottom-0 h-[2px] rounded-full"
          style={{ background: "var(--blue-700)" }}
        />
      )}
    </button>
  );
}

function LangPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 rounded-full transition-colors"
      style={{
        background: active ? "#fff" : "transparent",
        color: active ? "var(--blue-900)" : "rgba(255,255,255,.7)",
      }}
    >
      {children}
    </button>
  );
}

function Section({ num, title, children }: { num: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-7 last:mb-0">
      <h3 className="text-[18px] font-semibold mb-3 flex items-baseline gap-3" style={{ color: "var(--ink)" }}>
        <span
          className="text-xs font-bold tracking-widest"
          style={{ color: "var(--blue-700)", fontFamily: "'JetBrains Mono', monospace" }}
        >
          {num}
        </span>
        {title}
      </h3>
      <div className="text-[15px] leading-relaxed space-y-3" style={{ color: "var(--ink-2)" }}>
        {children}
      </div>
    </section>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span
            className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ background: "var(--blue-700)" }}
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

// ─── Terms — Thai ───────────────────────────────────────────
function TermsContentTH() {
  return (
    <div>
      <h2 className="text-[28px] font-bold mb-2 tracking-tight" style={{ color: "var(--ink)" }}>
        เงื่อนไขการใช้งาน
      </h2>
      <p className="text-[14px] mb-9" style={{ color: "var(--ink-3)" }}>
        ข้อตกลงในการใช้บริการ FlowCloser ระหว่างท่าน (เจ้าของร้าน) กับ บริษัท ซุปเปอร์ เฟิร์ม จำกัด
      </p>

      <Section num="01" title="ผู้ให้บริการและคำนิยาม">
        <p>
          บริการ FlowCloser ดำเนินการโดย <b>บริษัท ซุปเปอร์ เฟิร์ม จำกัด</b>{" "}
          (SUPER FIRM CO., LTD.) เลขทะเบียนนิติบุคคล{" "}
          <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>0105565082136</span> (ในเอกสารนี้เรียก "เรา" หรือ "บริษัท")
        </p>
        <p>คำนิยามที่ใช้ในเอกสารนี้:</p>
        <Bullets
          items={[
            <><b>"บริการ"</b> หมายถึง แพลตฟอร์ม FlowCloser ทั้งเว็บไซต์ ระบบหลังบ้าน API และฟีเจอร์ทั้งหมดที่ให้บริการแก่ท่าน</>,
            <><b>"ท่าน" / "เจ้าของร้าน"</b> หมายถึง ผู้ลงทะเบียนใช้บริการในนามตนเองหรือนิติบุคคล (ลูกค้าของเรา)</>,
            <><b>"ผู้ใช้ปลายทาง"</b> หมายถึง ลูกค้าของท่านที่ติดต่อสอบถามและสั่งซื้อสินค้าของท่านผ่านช่องทาง Facebook/LINE/เว็บไซต์</>,
            <><b>"เนื้อหา"</b> หมายถึง ข้อมูลร้าน รายการสินค้า รูปภาพ ข้อความสนทนา และข้อมูลอื่นที่เกิดจากการใช้บริการ</>,
          ]}
        />
      </Section>

      <Section num="02" title="การยอมรับข้อตกลงและคุณสมบัติผู้ใช้">
        <p>
          เมื่อสมัครหรือใช้บริการ ถือว่าท่านได้อ่าน เข้าใจ และยอมรับเงื่อนไขทั้งหมดในเอกสารนี้
          รวมถึง <Link href="/terms" className="hover:underline" style={{ color: "var(--blue-700)" }}>นโยบายความเป็นส่วนตัว</Link>
        </p>
        <p>ท่านยืนยันว่า:</p>
        <Bullets
          items={[
            "ท่านอายุ 20 ปีบริบูรณ์ขึ้นไป หรือเป็นผู้แทนที่ได้รับมอบอำนาจของนิติบุคคลที่ใช้บริการ",
            "ข้อมูลที่ให้กับเรามีความถูกต้องและเป็นปัจจุบัน",
            "ท่านมีสิทธิ์ตามกฎหมายในการประกอบธุรกิจขายสินค้า/บริการที่ใช้แพลตฟอร์มนี้จัดการ",
          ]}
        />
      </Section>

      <Section num="03" title="คำอธิบายบริการ">
        <p>
          FlowCloser เป็นแพลตฟอร์ม Software-as-a-Service (SaaS) ที่ช่วยให้ร้านค้าออนไลน์ใช้ AI ตอบลูกค้าและช่วยปิดการขายผ่านช่องทาง:
        </p>
        <Bullets
          items={[
            "Facebook Messenger (เพจที่ท่านเป็นแอดมิน)",
            "LINE Official Account ของท่าน",
            "หน้าเว็บไซต์ขาย (Sale Page) ที่สร้างผ่านระบบ",
            "Webhook สำหรับเชื่อมต่อระบบภายนอก",
          ]}
        />
        <p>
          ระบบจะวิเคราะห์ข้อความที่ผู้ใช้ปลายทางส่งเข้ามา ตอบกลับตามคำสั่ง/บุคลิกที่ท่านกำหนด สร้างคำสั่งซื้อ
          ตรวจสลิปการโอน และส่งต่อข้อมูลให้ท่านในแดชบอร์ด
          เราพัฒนาและปรับปรุงบริการอย่างต่อเนื่อง — ฟีเจอร์อาจเปลี่ยนแปลงตามที่เห็นว่าเหมาะสม
        </p>
      </Section>

      <Section num="04" title="การลงทะเบียนและการรักษาบัญชี">
        <Bullets
          items={[
            "ท่านต้องลงทะเบียนด้วยอีเมลและรหัสผ่าน หรือ Google Account",
            "ท่านมีหน้าที่รักษาความลับของรหัสผ่าน Token และ API Key",
            "ท่านรับผิดชอบต่อกิจกรรมทั้งหมดที่เกิดขึ้นภายใต้บัญชีของท่าน",
            "หากพบการเข้าถึงโดยไม่ได้รับอนุญาต กรุณาแจ้งเราทันที",
            "ห้ามแชร์บัญชีกับบุคคลอื่น (1 บัญชี = 1 ผู้ใช้)",
          ]}
        />
      </Section>

      <Section num="05" title="ค่าบริการ การชำระเงิน และนโยบายไม่คืนเงิน">
        <Bullets
          items={[
            "อัตราค่าบริการเป็นไปตามแพ็กเกจที่ท่านเลือก ณ เวลาสมัครใช้งาน แสดงผ่านหน้าแพ็กเกจในแดชบอร์ด",
            "การชำระเงินดำเนินการผ่าน Stripe เราไม่จัดเก็บหมายเลขบัตรเครดิตของท่าน",
            "ค่าบริการรายเดือน/รายปีจะถูกเรียกเก็บล่วงหน้า",
            "เครดิตตรวจสลิป (Slip Credits) ที่ซื้อแล้วใช้ได้จนหมด ไม่หมดอายุ",
            "เราอาจเปลี่ยนแปลงอัตราค่าบริการในอนาคต โดยแจ้งล่วงหน้าอย่างน้อย 30 วัน หากท่านไม่ยอมรับ ท่านมีสิทธิ์ยกเลิกบริการก่อนอัตราใหม่มีผล",
          ]}
        />
        <p>
          <b>นโยบายไม่คืนเงิน (No Refund Policy):</b> ค่าบริการ ค่าสมาชิก และเครดิตทุกประเภทที่ชำระแล้ว
          ถือเป็นที่สิ้นสุดและ <b>ไม่สามารถขอคืนเงินได้ในทุกกรณี</b> ไม่ว่าจะเป็นการยกเลิกกลางรอบบิล
          การไม่ได้ใช้งาน การใช้งานบางส่วน หรือการเปลี่ยนใจหลังการชำระเงิน
        </p>
        <p>
          ก่อนซื้อแพ็กเกจรายเดือน/รายปี เราแนะนำให้ท่านทดลองใช้งานผ่านแพ็กเกจ <b>FREE</b> ก่อน
          เพื่อตรวจสอบว่าบริการตรงกับความต้องการของท่าน
        </p>
      </Section>

      <Section num="06" title="การใช้งานที่ยอมรับได้">
        <p>ท่านตกลงว่าจะ <b>ไม่</b>:</p>
        <Bullets
          items={[
            "ใช้บริการเพื่อกิจกรรมที่ผิดกฎหมายของไทยหรือประเทศที่เกี่ยวข้อง",
            "ส่งสแปม ข้อความหลอกลวง หรือเนื้อหาคุกคาม/ละเมิดสิทธิ์ผ่านระบบ",
            "พยายามเจาะระบบ ทำ reverse engineering หรือ scrape API เกินขอบเขตที่อนุญาต",
            "ใช้ระบบสร้าง/เผยแพร่ข้อมูลเท็จ ปลอมแปลง หรือลอกเลียนแบรนด์อื่น",
            "ใช้ AI ของเราในการสร้างเนื้อหาที่อาจเป็นอันตราย ลามก หรือเลือกปฏิบัติ",
            "ใช้บริการเพื่อสร้างหรือเผยแพร่ malware/ไวรัส",
            "ส่งต่อ token, ข้อมูล cred ของลูกค้าปลายทาง หรือข้อมูล PII ไปยังบุคคลที่สามโดยไม่ได้รับอนุญาต",
          ]}
        />
      </Section>

      <Section num="07" title="สินค้า/บริการที่ห้ามขายผ่าน FlowCloser">
        <p>
          FlowCloser เชื่อมต่อกับ Facebook และ LINE ซึ่งมีนโยบายชัดเจน ท่านห้ามใช้บริการขายหรือโปรโมตสิ่งเหล่านี้:
        </p>
        <Bullets
          items={[
            "ยา วัตถุออกฤทธิ์ต่อจิตประสาท หรือยาที่ต้องสั่งจ่ายโดยแพทย์ที่ไม่มีใบอนุญาต",
            "สลากกินแบ่ง การพนัน อาวุธ วัตถุระเบิด สินค้าเลียนแบบ/ของปลอม",
            "บุหรี่ บุหรี่ไฟฟ้า สุรา (กรณีจำเป็น ต้องเป็นไปตามข้อกำหนดเฉพาะของแพลตฟอร์ม)",
            "เนื้อหาทางเพศ บริการทางเพศ หรือสื่อลามก",
            "ผลิตภัณฑ์ทางการแพทย์ที่กล่าวอ้างเกินจริงโดยไม่มีหลักฐาน",
            "สิ่งของที่ละเมิดทรัพย์สินทางปัญญาของบุคคลอื่น",
          ]}
        />
        <p>
          หากเราตรวจพบว่าท่านขายสินค้าต้องห้าม เราจะระงับบริการทันทีโดยไม่คืนเงิน
          และอาจรายงานต่อแพลตฟอร์มที่เกี่ยวข้อง (Facebook/LINE) หรือเจ้าหน้าที่ตามกฎหมาย
        </p>
      </Section>

      <Section num="08" title="การปฏิบัติตามข้อตกลงของแพลตฟอร์มบุคคลที่สาม">
        <p>
          เมื่อท่านเชื่อมต่อ FlowCloser กับ Facebook Page หรือ LINE Official Account
          ท่านยังคงต้องปฏิบัติตามข้อตกลงของแพลตฟอร์มเหล่านั้นด้วย:
        </p>
        <Bullets
          items={[
            <><a href="https://developers.facebook.com/terms" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: "var(--blue-700)" }}>Facebook Platform Terms</a> และ Messenger Platform Policy</>,
            <><a href="https://terms.line.me/line_developers" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: "var(--blue-700)" }}>LINE Developer Terms</a> และ Messaging API Policy</>,
            "ข้อกำหนดของผู้ให้บริการชำระเงิน (Stripe Services Agreement)",
          ]}
        />
        <p>
          การละเมิดข้อตกลงของแพลตฟอร์มเหล่านี้ถือว่าละเมิดเงื่อนไขของเราด้วย ท่านรับผิดชอบเองในกรณีถูกระงับเพจ/บัญชีโดยแพลตฟอร์มดังกล่าว
        </p>
      </Section>

      <Section num="09" title="ข้อจำกัดของ AI และความรับผิดชอบของท่าน">
        <p>
          AI ของเรา (ใช้โมเดล Gemini ผ่าน AIML API) <b>อาจตอบผิดพลาดหรือคลาดเคลื่อนได้</b> ตอบของ AI ไม่ใช่คำสัญญาทางกฎหมาย ราคา หรือเงื่อนไขผูกพันโดยอัตโนมัติ ท่านในฐานะเจ้าของร้านมีหน้าที่:
        </p>
        <Bullets
          items={[
            "ตรวจสอบและยืนยันคำสั่งซื้อก่อนจัดส่งสินค้าจริง",
            "กำหนดบุคลิก กฎ และคำสั่งของ AI ให้สอดคล้องกับธุรกิจและกฎหมาย",
            "ตรวจสอบสลิปและการชำระเงินซ้ำในกรณีที่ AI/ระบบตรวจสลิปอัตโนมัติให้ผลที่ไม่ชัด",
            "รับผิดชอบเนื้อหาและข้อมูลสินค้าที่ป้อนเข้าระบบ ซึ่งจะถูกใช้เป็นบริบทให้ AI",
          ]}
        />
        <p>
          เราไม่รับผิดชอบต่อความเสียหายอันเกิดจากการที่ท่านส่งสินค้าตามคำสั่งของ AI โดยไม่ได้ตรวจสอบ หรือกำหนดกฎที่ขัดต่อกฎหมาย
        </p>
      </Section>

      <Section num="10" title="ข้อมูลและทรัพย์สินทางปัญญา">
        <Bullets
          items={[
            <><b>ข้อมูลของท่าน:</b> ท่านยังคงเป็นเจ้าของข้อมูลและเนื้อหาที่อัปโหลด เราใช้เฉพาะเพื่อให้บริการตามที่ระบุใน <Link href="/terms" className="hover:underline" style={{ color: "var(--blue-700)" }}>นโยบายความเป็นส่วนตัว</Link></>,
            <><b>ข้อมูลของผู้ใช้ปลายทาง:</b> เราเป็นผู้ประมวลผลข้อมูลในนามของท่าน ไม่ใช่เจ้าของข้อมูล — ท่านมีหน้าที่แจ้ง/ขอความยินยอมจากผู้ใช้ปลายทางตามกฎหมายที่ใช้บังคับ</>,
            <><b>ทรัพย์สินทางปัญญาของเรา:</b> โลโก้ ชื่อ "FlowCloser" รหัสต้นฉบับ การออกแบบ UI และโมเดลภายใน เป็นทรัพย์สินของบริษัท ห้ามคัดลอก ดัดแปลง หรือนำไปใช้ในเชิงพาณิชย์โดยไม่ได้รับอนุญาต</>,
            <><b>ผลลัพธ์จาก AI:</b> ข้อความตอบกลับและคำสั่งซื้อที่ AI สร้างจากข้อมูลของท่าน เป็นของท่าน — ท่านสามารถใช้ในธุรกิจได้อย่างเสรี ตราบใดที่ปฏิบัติตามเงื่อนไขนี้</>,
          ]}
        />
      </Section>

      <Section num="11" title="ความพร้อมใช้งานของบริการ">
        <p>
          เรามุ่งให้บริการที่มีเสถียรภาพ แต่ <b>ไม่รับประกันว่าระบบจะให้บริการได้ตลอด 24 ชั่วโมงโดยไม่มีข้อผิดพลาด</b>
          อาจมีการหยุดให้บริการชั่วคราวสำหรับการบำรุงรักษา การอัปเดต หรือเหตุสุดวิสัย (เช่น ผู้ให้บริการคลาวด์ขัดข้อง, AI provider downtime, การโจมตีทางไซเบอร์)
          เป้าหมายของเราคือ uptime ที่ไม่น้อยกว่า 99% รายเดือน หากต่ำกว่านั้นท่านมีสิทธิ์ขอคืนเครดิตตามสัดส่วน (ดูข้อ 05)
        </p>
      </Section>

      <Section num="12" title="การรับประกันและข้อจำกัดความรับผิด">
        <p>
          เราให้บริการ "ตามสภาพที่เป็น" (AS IS) และ "ตามที่มี" (AS AVAILABLE) ไม่มีการรับประกันโดยนัยว่าบริการจะตรงตามวัตถุประสงค์เฉพาะของท่าน
        </p>
        <p>
          ภายใต้กฎหมายที่ใช้บังคับ ความรับผิดสูงสุดของบริษัทรวมในทุกกรณีจะไม่เกิน <b>ค่าบริการที่ท่านชำระให้เราในช่วง 12 เดือนก่อนเหตุที่เกิดขึ้น</b>
          เราไม่รับผิดชอบต่อความเสียหายทางอ้อม การสูญเสียรายได้ การสูญเสียโอกาสทางธุรกิจ หรือการสูญหายของข้อมูลที่ท่านควรสำรองไว้เอง
        </p>
      </Section>

      <Section num="13" title="การชดใช้ความเสียหาย (Indemnity)">
        <p>
          ท่านตกลงชดใช้ความเสียหายและปกป้องบริษัทจากข้อเรียกร้อง ความเสียหาย และค่าใช้จ่าย (รวมค่าทนายความ) ที่เกิดจาก:
        </p>
        <Bullets
          items={[
            "การที่ท่านใช้บริการโดยละเมิดเงื่อนไขนี้หรือกฎหมาย",
            "เนื้อหา/สินค้าของท่านที่ละเมิดทรัพย์สินทางปัญญาของบุคคลที่สาม",
            "ข้อพิพาทระหว่างท่านกับผู้ใช้ปลายทาง (เช่น สินค้าไม่ตรงปก จัดส่งช้า)",
          ]}
        />
        <p>
          บริษัทจะปกป้องท่านจากข้อเรียกร้องที่กล่าวว่าแพลตฟอร์ม FlowCloser เอง (ไม่รวมเนื้อหาของท่าน) ละเมิดทรัพย์สินทางปัญญาของบุคคลที่สาม
        </p>
      </Section>

      <Section num="14" title="การยกเลิกและระงับบริการ">
        <Bullets
          items={[
            "ท่านสามารถยกเลิกบริการได้ตลอดเวลาผ่านหน้าตั้งค่าบัญชีหรือติดต่อทีมสนับสนุน",
            "เมื่อยกเลิก ท่านจะใช้บริการต่อได้จนสิ้นสุดรอบบิลที่ชำระแล้ว ค่าบริการที่ชำระไปแล้วถือเป็นที่สิ้นสุดและไม่สามารถขอคืนได้ตามนโยบายในข้อ 05",
            "เราอาจระงับหรือยกเลิกบัญชีของท่านโดยทันที หากพบการละเมิดเงื่อนไขข้อ 06 หรือข้อ 07 โดยไม่มีการคืนเงินใด ๆ",
            "หลังจากยกเลิกบัญชี เราจะเก็บข้อมูลของท่านตามระยะเวลาในนโยบายความเป็นส่วนตัว ก่อนทำลายอย่างปลอดภัย",
          ]}
        />
      </Section>

      <Section num="15" title="การเปลี่ยนแปลงเงื่อนไข">
        <p>
          เราอาจปรับปรุงเงื่อนไขเป็นครั้งคราว การเปลี่ยนแปลงที่เป็นสาระสำคัญจะแจ้งล่วงหน้าอย่างน้อย <b>14 วัน</b> ผ่านอีเมลและการประกาศในระบบ
          การใช้บริการต่อหลังจากเงื่อนไขใหม่มีผลถือว่าท่านยอมรับ หากท่านไม่ยอมรับ ท่านมีสิทธิ์ยกเลิกบัญชีก่อนวันที่มีผล
        </p>
      </Section>

      <Section num="16" title="กฎหมายที่ใช้บังคับและการระงับข้อพิพาท">
        <p>
          เงื่อนไขนี้อยู่ภายใต้กฎหมายแห่งราชอาณาจักรไทย หากเกิดข้อพิพาท คู่กรณีตกลงพยายามเจรจาด้วยความสุจริตก่อน
          หากไม่สามารถตกลงกันได้ ให้คดีอยู่ในเขตอำนาจของศาลไทย
        </p>
      </Section>

      <Section num="17" title="ติดต่อ">
        <p>
          คำถามเกี่ยวกับเงื่อนไขการใช้งาน ติดต่อ:{" "}
          <a
            href="mailto:success@seaaii.com"
            className="hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            success@seaaii.com
          </a>{" "}
          หรือ{" "}
          <Link href="/contact" className="hover:underline" style={{ color: "var(--blue-700)" }}>
            หน้าติดต่อเรา
          </Link>
        </p>
      </Section>
    </div>
  );
}

// ─── Terms — English ────────────────────────────────────────
function TermsContentEN() {
  return (
    <div>
      <h2 className="text-[28px] font-bold mb-2 tracking-tight" style={{ color: "var(--ink)" }}>
        Terms of Service
      </h2>
      <p className="text-[14px] mb-9" style={{ color: "var(--ink-3)" }}>
        Service agreement between you (the merchant) and Super Firm Co., Ltd.
      </p>

      <Section num="01" title="Provider and Definitions">
        <p>
          The FlowCloser service is operated by <b>Super Firm Co., Ltd.</b>{" "}
          (SUPER FIRM CO., LTD.), Thai company registration number{" "}
          <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>0105565082136</span> ("we", "us", or the "Company").
        </p>
        <p>The following terms apply throughout this agreement:</p>
        <Bullets
          items={[
            <><b>"Service"</b> — the FlowCloser platform, including the website, dashboard, APIs, and all features made available to you.</>,
            <><b>"You" / "Merchant"</b> — the person or legal entity registering and using the Service.</>,
            <><b>"End User"</b> — your customer who contacts your shop or places orders through your connected Facebook page, LINE OA, sale page, or webhook.</>,
            <><b>"Content"</b> — store data, product listings, images, conversation logs, and any other data generated through your use of the Service.</>,
          ]}
        />
      </Section>

      <Section num="02" title="Acceptance and Eligibility">
        <p>
          By signing up for or using the Service, you confirm that you have read, understood, and agreed to these Terms,
          including our <Link href="/terms" className="hover:underline" style={{ color: "var(--blue-700)" }}>Privacy Policy</Link>.
        </p>
        <p>You also represent that:</p>
        <Bullets
          items={[
            "You are at least 20 years old, or are a duly authorised representative of the legal entity using the Service.",
            "The information you provide to us is accurate and current.",
            "You have the legal right to operate the business whose sales activities you manage through the Service.",
          ]}
        />
      </Section>

      <Section num="03" title="Description of the Service">
        <p>
          FlowCloser is a Software-as-a-Service (SaaS) platform that helps online merchants use AI to respond to customers and assist with closing sales across the following channels:
        </p>
        <Bullets
          items={[
            "Facebook Messenger (pages on which you are an admin)",
            "LINE Official Account",
            "Sale Pages built within the platform",
            "Webhooks for connecting external systems",
          ]}
        />
        <p>
          The system analyses messages from End Users, replies according to the persona and rules you configure, creates orders, verifies payment slips, and surfaces the data to you in the dashboard.
          We continuously improve the Service — features may evolve as we see fit.
        </p>
      </Section>

      <Section num="04" title="Account Registration and Security">
        <Bullets
          items={[
            "You register using an email and password, or via a Google Account.",
            "You are responsible for keeping your password, tokens, and API keys confidential.",
            "You are responsible for all activity that occurs under your account.",
            "If you discover unauthorised access, notify us immediately.",
            "Account sharing is not allowed (one account per user).",
          ]}
        />
      </Section>

      <Section num="05" title="Fees, Payment, and No-Refund Policy">
        <Bullets
          items={[
            "Fees follow the plan you choose at the time of subscription, as displayed in the dashboard.",
            "Payments are processed through Stripe; we do not store your card numbers.",
            "Monthly or annual fees are charged in advance.",
            "Slip-check credits are valid until consumed and do not expire.",
            "We may change fees in the future with at least 30 days' prior notice; if you do not accept the new rate, you may cancel before it takes effect.",
          ]}
        />
        <p>
          <b>No-Refund Policy:</b> All fees, subscription charges, and credits, once paid, are
          <b> final and non-refundable under any circumstances</b>, including but not limited to mid-cycle
          cancellation, non-use, partial use, or change of mind after payment.
        </p>
        <p>
          Before subscribing to a paid monthly or annual plan, we recommend evaluating the Service through
          our <b>FREE</b> plan to confirm it meets your needs.
        </p>
      </Section>

      <Section num="06" title="Acceptable Use">
        <p>You agree that you will <b>not</b>:</p>
        <Bullets
          items={[
            "Use the Service for activities unlawful under the laws of Thailand or any applicable jurisdiction.",
            "Send spam, deceptive messages, or threatening / rights-infringing content through the system.",
            "Attempt to penetrate the system, reverse engineer, or scrape the API beyond permitted limits.",
            "Use the system to create or distribute false information, impersonations, or counterfeits of other brands.",
            "Use our AI to produce harmful, sexually explicit, or discriminatory content.",
            "Use the Service to create or distribute malware or viruses.",
            "Forward tokens, end-user credentials, or PII to third parties without authorisation.",
          ]}
        />
      </Section>

      <Section num="07" title="Prohibited Goods and Services">
        <p>
          FlowCloser connects to Facebook and LINE, both of which have strict policies. You may not use the Service to sell or promote:
        </p>
        <Bullets
          items={[
            "Drugs, psychoactive substances, or prescription medication sold without proper licensing.",
            "Lotteries, gambling, weapons, explosives, counterfeit or knock-off goods.",
            "Cigarettes, e-cigarettes, or alcohol (only allowed where the relevant platform's specific requirements are met).",
            "Sexual content, sexual services, or pornographic media.",
            "Health products with unsubstantiated claims.",
            "Items that infringe a third party's intellectual property.",
          ]}
        />
        <p>
          If we discover that you sell prohibited items, we will suspend the Service immediately and without refund, and may report you to the relevant platform (Facebook/LINE) or to authorities.
        </p>
      </Section>

      <Section num="08" title="Compliance with Third-Party Platforms">
        <p>
          When you connect FlowCloser to a Facebook Page or LINE Official Account, you also remain bound by those platforms' agreements:
        </p>
        <Bullets
          items={[
            <><a href="https://developers.facebook.com/terms" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: "var(--blue-700)" }}>Facebook Platform Terms</a> and Messenger Platform Policy</>,
            <><a href="https://terms.line.me/line_developers" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: "var(--blue-700)" }}>LINE Developer Terms</a> and Messaging API Policy</>,
            "Stripe Services Agreement",
          ]}
        />
        <p>
          A breach of those third-party agreements is also a breach of these Terms. You bear sole responsibility if your page or account is suspended by those platforms.
        </p>
      </Section>

      <Section num="09" title="AI Limitations and Your Responsibilities">
        <p>
          Our AI (built on Google's Gemini model via the AIML API) <b>may produce inaccurate or unexpected outputs</b>. AI replies do not constitute legally binding offers, prices, or commitments by themselves. As the merchant, you are responsible for:
        </p>
        <Bullets
          items={[
            "Reviewing and confirming orders before fulfilment.",
            "Configuring AI persona, rules, and instructions consistent with your business and applicable laws.",
            "Re-checking payment slips and transfers when the AI / automatic verifier returns an unclear result.",
            "The accuracy of product information and content you provide, which the AI uses as context.",
          ]}
        />
        <p>
          We are not liable for losses arising from your fulfilling orders based solely on AI outputs without verification, or from instructions you give the AI that conflict with the law.
        </p>
      </Section>

      <Section num="10" title="Data and Intellectual Property">
        <Bullets
          items={[
            <><b>Your data:</b> You retain ownership of the data and content you upload. We use it solely to provide the Service in line with our <Link href="/terms" className="hover:underline" style={{ color: "var(--blue-700)" }}>Privacy Policy</Link>.</>,
            <><b>End-user data:</b> We act as Processor on your behalf — we are not the owner of that data. You are responsible for notifying End Users and obtaining their consent as required by applicable law.</>,
            <><b>Our IP:</b> The "FlowCloser" name and logo, source code, UI design, and internal models are property of the Company. They may not be copied, modified, or used commercially without our written permission.</>,
            <><b>AI outputs:</b> Replies and orders generated by the AI from your data belong to you. You may freely use them in your business so long as these Terms are honoured.</>,
          ]}
        />
      </Section>

      <Section num="11" title="Service Availability">
        <p>
          We strive to provide a stable Service but <b>do not guarantee uninterrupted, error-free operation 24/7</b>.
          The Service may be temporarily unavailable for maintenance, updates, or events beyond our control (e.g. cloud-provider outages, AI-provider downtime, cyberattacks).
          Our target is at least 99% monthly uptime; if we fall below that, you may request pro-rated credit (see Section 5).
        </p>
      </Section>

      <Section num="12" title="Warranty and Limitation of Liability">
        <p>
          The Service is provided "AS IS" and "AS AVAILABLE", without any implied warranty that it will meet your particular requirements.
        </p>
        <p>
          To the extent permitted by applicable law, our aggregate liability under these Terms in any case shall not exceed <b>the fees you paid us in the 12 months preceding the event giving rise to the claim</b>.
          We are not liable for indirect damages, lost revenue, lost business opportunity, or loss of data that you should have backed up.
        </p>
      </Section>

      <Section num="13" title="Indemnification">
        <p>
          You agree to indemnify and defend the Company from claims, damages, and expenses (including reasonable legal fees) arising from:
        </p>
        <Bullets
          items={[
            "Your use of the Service in violation of these Terms or applicable law.",
            "Content or goods you provide that infringe a third party's intellectual property.",
            "Disputes between you and your End Users (e.g. mismatched goods, late delivery).",
          ]}
        />
        <p>
          The Company will defend you against claims that the FlowCloser platform itself (excluding your content) infringes a third party's intellectual property rights.
        </p>
      </Section>

      <Section num="14" title="Suspension and Termination">
        <Bullets
          items={[
            "You may cancel the Service at any time via the account settings page or by contacting support.",
            "On cancellation, you may continue to use the Service until the end of the paid billing period; pre-paid fees are final and non-refundable as stated in Section 5.",
            "We may suspend or terminate your account immediately upon a breach of Section 6 or Section 7, without any refund.",
            "After termination, we retain your data for the periods stated in the Privacy Policy before secure deletion.",
          ]}
        />
      </Section>

      <Section num="15" title="Changes to These Terms">
        <p>
          We may update these Terms from time to time. Material changes will be announced at least <b>14 days</b> in advance via email and an in-app banner.
          Continued use after the new version takes effect constitutes acceptance. If you do not accept, you may cancel before the effective date.
        </p>
      </Section>

      <Section num="16" title="Governing Law and Dispute Resolution">
        <p>
          These Terms are governed by the laws of the Kingdom of Thailand. In the event of a dispute, the parties shall first attempt to resolve it in good faith;
          failing that, the dispute shall be subject to the exclusive jurisdiction of the Thai courts.
        </p>
      </Section>

      <Section num="17" title="Contact">
        <p>
          Questions about these Terms can be sent to:{" "}
          <a
            href="mailto:success@seaaii.com"
            className="hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            success@seaaii.com
          </a>{" "}
          or via our{" "}
          <Link href="/contact" className="hover:underline" style={{ color: "var(--blue-700)" }}>
            contact page
          </Link>.
        </p>
      </Section>
    </div>
  );
}

// ─── Privacy — Thai ─────────────────────────────────────────
function PrivacyContentTH() {
  return (
    <div>
      <h2 className="text-[28px] font-bold mb-2 tracking-tight" style={{ color: "var(--ink)" }}>
        นโยบายความเป็นส่วนตัว
      </h2>
      <p className="text-[14px] mb-9" style={{ color: "var(--ink-3)" }}>
        จัดทำตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 (PDPA)
      </p>

      <Section num="01" title="ผู้ควบคุมข้อมูลส่วนบุคคล">
        <p>
          ผู้ให้บริการแพลตฟอร์ม FlowCloser และผู้ควบคุมข้อมูลส่วนบุคคล (Data Controller) ของท่านคือ:
        </p>
        <div
          className="rounded-xl p-4 mt-2"
          style={{ background: "var(--bg-soft)", border: "1px solid var(--line)" }}
        >
          <p className="text-[14px]">
            <b>บริษัท ซุปเปอร์ เฟิร์ม จำกัด</b>
            <br />
            (SUPER FIRM CO., LTD.)
            <br />
            เลขทะเบียนนิติบุคคล: <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>0105565082136</span>
            <br />
            ที่อยู่: 281/95 ถนนวิภาวดีรังสิต แขวงสนามบิน เขตดอนเมือง กรุงเทพมหานคร 10210
            <br />
            อีเมลติดต่อเรื่องข้อมูลส่วนบุคคล (DPO):{" "}
            <a
              href="mailto:success@seaaii.com"
              className="hover:underline"
              style={{ color: "var(--blue-700)" }}
            >
              success@seaaii.com
            </a>
          </p>
        </div>
      </Section>

      <Section num="02" title="บทบาทของเราและขอบเขตของนโยบาย">
        <p>FlowCloser เป็นแพลตฟอร์ม SaaS ที่ช่วยให้ร้านค้าออนไลน์ใช้ AI ตอบลูกค้าและปิดการขาย เรามีบทบาท 2 ลักษณะ:</p>
        <Bullets
          items={[
            <><b>ผู้ควบคุมข้อมูล (Data Controller):</b> สำหรับ "เจ้าของร้าน" ที่สมัครและใช้งานบริการของเรา (ข้อมูลบัญชี การชำระเงิน การใช้งานระบบ)</>,
            <><b>ผู้ประมวลผลข้อมูล (Data Processor):</b> สำหรับ "ข้อความและข้อมูลของลูกค้าปลายทาง" ที่ลูกค้าส่งเข้ามายังเพจ/บัญชีของร้าน — เราประมวลผลข้อมูลเหล่านี้ในนามของร้านเพื่อให้ AI ตอบกลับ ข้อมูลเป็นของร้านและของลูกค้าปลายทาง ไม่ใช่ของเรา</>,
          ]}
        />
      </Section>

      <Section num="03" title="ข้อมูลที่เราเก็บรวบรวม">
        <p>เราเก็บรวบรวมข้อมูลต่อไปนี้:</p>
        <Bullets
          items={[
            <><b>ข้อมูลบัญชีเจ้าของร้าน:</b> ชื่อ อีเมล รหัสผ่านที่เข้ารหัสแล้ว (bcrypt) เบอร์โทรศัพท์ รูปโปรไฟล์ และข้อมูลที่ได้รับจาก Google Account หากเลือก login ผ่าน Google</>,
            <><b>ข้อมูลร้านค้า:</b> ชื่อร้าน รายละเอียด ที่อยู่ เลขประจำตัวผู้เสียภาษี เบอร์โทรศัพท์ บัญชีธนาคารเพื่อรับชำระเงิน รายการสินค้า ราคา รูปภาพสินค้า โปรโมชั่น</>,
            <><b>ข้อมูลการใช้งาน:</b> วันเวลาเข้าใช้งาน การตั้งค่า AI ประวัติการสั่งซื้อในระบบ จำนวน token AI ที่ใช้</>,
            <><b>ข้อมูลการชำระเงิน:</b> ระดับแพ็กเกจที่สมัคร ประวัติการชำระเงินผ่าน Stripe (เราไม่จัดเก็บหมายเลขบัตรเครดิตเอง — Stripe เป็นผู้จัดเก็บ)</>,
            <><b>ข้อมูลทางเทคนิค:</b> IP address, User-Agent, log การเข้าใช้งานระบบ เพื่อความปลอดภัยและตรวจสอบเหตุการณ์ผิดปกติ</>,
            <><b>ข้อมูลจากแพลตฟอร์มผู้ส่งสาร (Messaging):</b> ดูข้อ 04</>,
          ]}
        />
      </Section>

      <Section num="04" title="ข้อมูลจาก Facebook Messenger และ LINE Official Account">
        <p>
          เมื่อร้านเชื่อมต่อ FlowCloser กับ Facebook Page หรือ LINE Official Account เพื่อให้ AI ของเราตอบลูกค้าให้
          เราจะรับและประมวลผลข้อมูลต่อไปนี้ <b>ในนามของร้าน (เป็น Data Processor)</b>:
        </p>
        <Bullets
          items={[
            <><b>ข้อมูลเพจ/บัญชี:</b> Page ID, Page Access Token, Page Name (ใช้เพื่อยืนยันสิทธิ์ในการตอบกลับเพจ)</>,
            <><b>ข้อมูลลูกค้าปลายทาง:</b> ชื่อแสดง (Display Name) รูปโปรไฟล์ Page-Scoped User ID (PSID/LINE userId)</>,
            <><b>เนื้อหาข้อความ:</b> ข้อความ รูปภาพ และไฟล์ที่ลูกค้าส่งเข้ามายังเพจ/บัญชีของร้าน</>,
            <><b>ข้อมูลคำสั่งซื้อ:</b> ที่อยู่จัดส่ง เบอร์โทรศัพท์ และข้อมูลอื่นที่ลูกค้าให้กับร้านเพื่อใช้สั่งซื้อ</>,
          ]}
        />
        <p>
          เราจะใช้ข้อมูลเหล่านี้เฉพาะ (ก) เพื่อให้ AI ตอบลูกค้าและสร้างคำสั่งซื้อในนามของร้าน
          (ข) เพื่อบันทึกประวัติการสนทนาให้ร้านดูย้อนหลังในแดชบอร์ด และ (ค) เพื่อ debug/แก้ไขปัญหาทางเทคนิคเมื่อจำเป็น
          <b>เราไม่ใช้ข้อความของลูกค้าปลายทางเพื่อทำการตลาด ส่งโฆษณา ขายต่อให้บุคคลที่สาม หรือเพื่อเทรนโมเดล AI ของเรา</b>
        </p>
      </Section>

      <Section num="05" title="วัตถุประสงค์และฐานทางกฎหมาย">
        <Bullets
          items={[
            <><b>การให้บริการ (สัญญา):</b> ดำเนินการตามสัญญาบริการ FlowCloser กับเจ้าของร้าน</>,
            <><b>ตอบกลับลูกค้าปลายทาง (สัญญา/ประโยชน์โดยชอบด้วยกฎหมาย):</b> ประมวลผลข้อความเพื่อให้ AI ตอบลูกค้าตามคำสั่งของร้าน</>,
            <><b>ความปลอดภัยและกันการฉ้อโกง (ประโยชน์โดยชอบด้วยกฎหมาย):</b> ตรวจสอบ log, rate limit, ตรวจสอบสลิปการโอน</>,
            <><b>การปรับปรุงระบบ (ประโยชน์โดยชอบด้วยกฎหมาย):</b> วิเคราะห์ภาพรวมการใช้งานในระดับ aggregate ที่ระบุตัวบุคคลไม่ได้</>,
            <><b>การปฏิบัติตามกฎหมาย:</b> เช่น เก็บใบกำกับภาษี ประวัติการชำระเงินตามกฎหมายภาษีของไทย</>,
            <><b>การติดต่อสื่อสาร (ความยินยอม):</b> ส่งอีเมลแจ้งข่าว/อัปเดตบริการ ท่านยกเลิกได้ตลอดเวลา</>,
          ]}
        />
      </Section>

      <Section num="06" title="การใช้บริการบุคคลที่สาม (Subprocessors)">
        <p>เพื่อให้บริการ เราใช้ผู้ให้บริการบุคคลที่สามต่อไปนี้ ทุกรายมีข้อตกลงรักษาความลับและมาตรฐานความปลอดภัย:</p>
        <Bullets
          items={[
            <><b>Google LLC / AIML API:</b> ประมวลผลข้อความผ่านโมเดล Gemini เพื่อสร้างคำตอบ AI (ข้อมูลที่ส่งจะไม่ถูกใช้เทรนโมเดลตาม policy ของผู้ให้บริการ)</>,
            <><b>Meta Platforms, Inc.:</b> Facebook Messenger Platform — รับ/ส่งข้อความกับเพจของร้าน</>,
            <><b>LINE Corporation:</b> LINE Messaging API — รับ/ส่งข้อความกับ LINE Official Account ของร้าน</>,
            <><b>Cloudflare R2:</b> จัดเก็บไฟล์รูปภาพสินค้าและสลิปการโอน</>,
            <><b>Stripe, Inc.:</b> ประมวลผลการชำระเงินค่าบริการ (เราไม่เห็นหมายเลขบัตรเครดิต)</>,
            <><b>Supabase / ผู้ให้บริการฐานข้อมูล MySQL:</b> เก็บข้อมูลร้าน บัญชี และคำสั่งซื้อ (server ตั้งอยู่ในประเทศไทย)</>,
            <><b>ผู้ให้บริการอีเมล SMTP:</b> ส่งอีเมลยืนยัน/รีเซ็ตรหัสผ่าน/แจ้งเตือนระบบ</>,
            <><b>EasySlip API:</b> ตรวจสอบความถูกต้องของสลิปโอนเงิน (ใช้เฉพาะเมื่อร้านเปิดใช้งาน)</>,
          ]}
        />
      </Section>

      <Section num="07" title="การส่งข้อมูลข้ามประเทศ">
        <p>
          ข้อมูลฐานข้อมูลหลักของ FlowCloser ตั้งอยู่ใน<b>ประเทศไทย</b>
          อย่างไรก็ตาม การประมวลผลผ่าน AI provider (Google/AIML), Meta, LINE, Cloudflare, Stripe และผู้ให้บริการอื่น
          อาจมีการส่งข้อมูลออกไปประมวลผลในต่างประเทศ (เช่น สหรัฐอเมริกา สิงคโปร์ ญี่ปุ่น)
          ผู้ให้บริการเหล่านี้ปฏิบัติตามมาตรฐานสากล เช่น GDPR, ISO/IEC 27001, SOC 2
          ซึ่งให้ความคุ้มครองที่ใกล้เคียงกับ PDPA ของประเทศไทย
        </p>
      </Section>

      <Section num="08" title="ระยะเวลาในการเก็บข้อมูล">
        <Bullets
          items={[
            <><b>บัญชีเจ้าของร้าน:</b> ตลอดอายุการใช้งาน + 90 วันหลังจากปิดบัญชี (เผื่อกู้คืน)</>,
            <><b>ข้อความสนทนาและคำสั่งซื้อ:</b> 12 เดือนนับจากวันที่สนทนาล่าสุด เพื่อให้ร้านดูย้อนหลังได้</>,
            <><b>สลิปการโอนและไฟล์ที่ลูกค้าอัปโหลด:</b> 90 วัน หลังจากนั้นจะถูกลบโดยอัตโนมัติ</>,
            <><b>Log ระบบและ IP:</b> 30 วัน เพื่อความปลอดภัยและตรวจสอบเหตุการณ์</>,
            <><b>เอกสารทางการเงิน/ภาษี:</b> 5 ปีตามที่กฎหมายไทยกำหนด</>,
            <><b>Token รีเซ็ตรหัสผ่าน/ยืนยันอีเมล:</b> 1 ชั่วโมง แล้วลบทิ้ง</>,
          ]}
        />
      </Section>

      <Section num="09" title="มาตรการรักษาความปลอดภัย">
        <Bullets
          items={[
            "การเข้ารหัสข้อมูลขณะส่งผ่าน (HTTPS/TLS) และขณะจัดเก็บที่จุดสำคัญ (รหัสผ่านด้วย bcrypt, token ด้วย SHA-256)",
            "การจำกัดการเข้าถึงข้อมูลตามหลัก least-privilege — เฉพาะทีมงานที่จำเป็นเท่านั้น",
            "Rate limiting และระบบกันการเข้าถึงผิดปกติ (account enumeration, brute force)",
            "การตรวจสอบ webhook signature ของ Facebook/LINE เพื่อกันข้อมูลปลอม",
            "การสำรองข้อมูลและทบทวนระบบความปลอดภัยเป็นระยะ",
            "การยืนยันตัวตนผ่าน OAuth (Google) หรือรหัสผ่านที่เข้ารหัสแล้ว",
          ]}
        />
      </Section>

      <Section num="10" title="สิทธิของเจ้าของข้อมูลส่วนบุคคล">
        <p>ตาม PDPA ท่านมีสิทธิดังนี้:</p>
        <Bullets
          items={[
            <><b>สิทธิเข้าถึง:</b> ขอสำเนาข้อมูลส่วนบุคคลของท่านที่เราเก็บไว้</>,
            <><b>สิทธิแก้ไข:</b> ขอให้แก้ไขข้อมูลที่ไม่ถูกต้องหรือไม่ครบถ้วน</>,
            <><b>สิทธิลบ:</b> ขอให้ลบข้อมูลในกรณีที่ไม่จำเป็นต้องเก็บอีกต่อไปหรือถอนความยินยอม</>,
            <><b>สิทธิจำกัดการประมวลผล:</b> ขอให้ระงับการใช้ข้อมูลในบางกรณี</>,
            <><b>สิทธิคัดค้าน:</b> คัดค้านการประมวลผลข้อมูลในบางวัตถุประสงค์</>,
            <><b>สิทธิโอนย้ายข้อมูล:</b> ขอรับข้อมูลในรูปแบบที่อ่านได้ด้วยเครื่องเพื่อโอนไปยังผู้ให้บริการรายอื่น</>,
            <><b>สิทธิถอนความยินยอม:</b> ถอนความยินยอมที่ให้ไว้ก่อนหน้าได้ตลอดเวลา</>,
            <><b>สิทธิยื่นเรื่องร้องเรียน:</b> ต่อสำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (สคส.)</>,
          ]}
        />
        <p>
          วิธีใช้สิทธิ: ส่งคำขอมาที่{" "}
          <a
            href="mailto:success@seaaii.com"
            className="hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            success@seaaii.com
          </a>{" "}
          หรือใช้ช่องทาง{" "}
          <Link href="/data-deletion" className="hover:underline" style={{ color: "var(--blue-700)" }}>
            ขอลบข้อมูล
          </Link>{" "}
          เราจะตอบกลับภายใน 30 วัน
        </p>
      </Section>

      <Section num="11" title="คุกกี้และเทคโนโลยีติดตาม">
        <p>เราใช้คุกกี้เพื่อ:</p>
        <Bullets
          items={[
            "จดจำเซสชันการเข้าสู่ระบบ (cookie ของ NextAuth) — จำเป็นต่อการใช้งาน",
            "จดจำการตั้งค่าและร้านที่เลือกใช้งานปัจจุบัน",
            "ป้องกัน CSRF และรักษาความปลอดภัยของระบบ",
          ]}
        />
        <p>เราไม่ใช้คุกกี้เพื่อโฆษณาหรือ third-party tracking ท่านสามารถจัดการคุกกี้ผ่านเบราว์เซอร์ของท่านได้ตลอดเวลา</p>
      </Section>

      <Section num="12" title="ผู้เยาว์">
        <p>
          บริการ FlowCloser ออกแบบมาสำหรับเจ้าของธุรกิจที่มีอายุไม่ต่ำกว่า 20 ปีบริบูรณ์
          เราไม่เก็บรวบรวมข้อมูลของผู้ที่อายุต่ำกว่า 20 ปีโดยเจตนา หากพบ เราจะลบข้อมูลโดยทันที
        </p>
      </Section>

      <Section num="13" title="การแจ้งเหตุละเมิดข้อมูล">
        <p>
          กรณีเกิดเหตุละเมิดข้อมูลส่วนบุคคลที่อาจมีความเสี่ยงต่อสิทธิและเสรีภาพของบุคคล
          เราจะแจ้งสำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (สคส.) <b>ภายใน 72 ชั่วโมง</b>
          และแจ้งผู้ใช้งานที่ได้รับผลกระทบโดยตรงผ่านอีเมลหรือการแจ้งเตือนในระบบโดยเร็วที่สุด
        </p>
      </Section>

      <Section num="14" title="การเปลี่ยนแปลงนโยบาย">
        <p>
          เราอาจปรับปรุงนโยบายนี้เป็นครั้งคราว
          การเปลี่ยนแปลงที่เป็นสาระสำคัญจะแจ้งให้ทราบล่วงหน้าอย่างน้อย 14 วันผ่านอีเมลและแบนเนอร์ในระบบ
          การใช้บริการต่อหลังจากการเปลี่ยนแปลงมีผลบังคับใช้ ถือว่าท่านยอมรับนโยบายฉบับใหม่
        </p>
      </Section>

      <Section num="15" title="ติดต่อเรา (DPO)">
        <p>หากมีคำถามหรือต้องการใช้สิทธิตาม PDPA ติดต่อเจ้าหน้าที่คุ้มครองข้อมูลส่วนบุคคลของเรา:</p>
        <div
          className="rounded-xl p-4 mt-2"
          style={{ background: "var(--bg-soft)", border: "1px solid var(--line)" }}
        >
          <p className="text-[14px]">
            <b>บริษัท ซุปเปอร์ เฟิร์ม จำกัด</b>
            <br />
            281/95 ถนนวิภาวดีรังสิต แขวงสนามบิน เขตดอนเมือง กรุงเทพมหานคร 10210
            <br />
            อีเมล:{" "}
            <a
              href="mailto:success@seaaii.com"
              className="hover:underline"
              style={{ color: "var(--blue-700)" }}
            >
              success@seaaii.com
            </a>
            <br />
            หรือผ่าน{" "}
            <Link href="/contact" className="hover:underline" style={{ color: "var(--blue-700)" }}>
              หน้าติดต่อเรา
            </Link>
          </p>
        </div>
      </Section>

      <Section num="16" title="ข้อมูลจาก Google Account (สำหรับ Google OAuth Verification)">
        <p>
          เมื่อท่านเลือก "ลงชื่อเข้าใช้ด้วย Google" FlowCloser ใช้ Google OAuth 2.0 พร้อม scope
          มาตรฐาน <code>openid</code>, <code>profile</code>, และ <code>email</code> เท่านั้น
          หัวข้อนี้เปิดเผยรายละเอียดทั้งหมดของการเข้าถึง การใช้งาน การจัดเก็บ และการแบ่งปันข้อมูล
          Google user data ตามที่{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            Google API Services User Data Policy
          </a>
          {" "}กำหนด
        </p>

        <h3 className="text-[16px] font-semibold mt-4 mb-1">ข้อมูลที่เราเข้าถึง (Data Accessed)</h3>
        <p>ผ่าน scope <code>openid profile email</code> เราเข้าถึงเฉพาะข้อมูลโปรไฟล์พื้นฐาน (non-sensitive) ต่อไปนี้:</p>
        <Bullets
          items={[
            "อีเมล (email) — ใช้เป็นตัวระบุบัญชีหลักของ FlowCloser",
            "ชื่อแสดง (display name) — แสดงในแดชบอร์ดเพื่อให้ทีมงานในร้านเดียวกันรู้ว่าใครออนไลน์อยู่",
            "URL รูปโปรไฟล์ Google (profile picture) — แสดงเป็น avatar ในแดชบอร์ด",
            "Google account unique identifier (sub) — ใช้เชื่อม Google sign-in กลับสู่บัญชีเดิมในครั้งต่อไป",
            "สถานะการยืนยันอีเมล (email_verified) — ใช้ตัดสินว่าต้องส่งอีเมลยืนยันเพิ่มไหม",
          ]}
        />
        <p>
          <b>เราไม่เข้าถึง ไม่ขอ และไม่จัดเก็บ</b>ข้อมูลต่อไปนี้:
        </p>
        <Bullets
          items={[
            "Gmail (ข้อความ ป้ายกำกับ ผู้ติดต่อในอีเมล)",
            "Google Calendar (กิจกรรม การนัดหมาย)",
            "Google Drive / Docs / Sheets / Slides (ไฟล์ทุกประเภท)",
            "Google Contacts (รายชื่อผู้ติดต่อ)",
            "YouTube (ประวัติ การ subscribe การกดไลค์)",
            "Google Photos / Google Fit / Google Pay / Maps history",
            "Sensitive หรือ restricted scopes ใด ๆ ของ Google API",
          ]}
        />

        <h3 className="text-[16px] font-semibold mt-4 mb-1">การใช้งาน (Data Usage)</h3>
        <p>ข้อมูลโปรไฟล์ Google ที่ระบุไว้ข้างต้น ใช้เฉพาะวัตถุประสงค์ต่อไปนี้เท่านั้น:</p>
        <Bullets
          items={[
            <><b>การยืนยันตัวตน (Authentication):</b> ตรวจสอบว่าท่านเป็นเจ้าของ Google Account นั้นจริงเมื่อ sign in</>,
            <><b>การสร้างหรือเชื่อมบัญชี (Account creation / linking):</b> สร้างบัญชีร้านค้าของท่านบน FlowCloser หรือ link เข้ากับบัญชีที่มีอยู่แล้วผ่านอีเมล</>,
            <><b>การแสดงผลในแดชบอร์ด (Display personalization):</b> แสดงชื่อและรูปโปรไฟล์ของท่านในแดชบอร์ด เพื่อให้ทีมงานในร้านระบุตัวกันได้</>,
          ]}
        />
        <p>
          <b>เราไม่ใช้</b>ข้อมูล Google user data เพื่อ:
        </p>
        <Bullets
          items={[
            "การโฆษณาหรือการตลาดที่ไม่เกี่ยวกับ FlowCloser",
            "การเทรนโมเดล AI / Machine Learning — ข้อมูล Google profile ไม่ถูกส่งให้ AI provider; เฉพาะเนื้อหาแชทที่ลูกค้าปลายทางพิมพ์เข้ามาเท่านั้นที่ถูกประมวลผลผ่าน AI",
            "การขาย เช่า หรือแลกเปลี่ยนข้อมูลให้บุคคลที่สาม",
            "การ enrich โปรไฟล์ข้ามเว็บไซต์ หรือ tracking ระหว่างแพลตฟอร์ม",
          ]}
        />

        <h3 className="text-[16px] font-semibold mt-4 mb-1">การจัดเก็บและระยะเวลา (Storage and Retention)</h3>
        <Bullets
          items={[
            "ข้อมูลโปรไฟล์ Google ถูกจัดเก็บใน database ของ FlowCloser บน server ในประเทศไทย พร้อมการเข้ารหัส (TLS ขณะส่ง, เข้ารหัสที่ disk level)",
            "เก็บไว้ตลอดอายุการใช้งานบัญชี + 90 วันหลังปิดบัญชี (เผื่อกู้คืน)",
            "ท่านสามารถลบบัญชีได้ทาง success@seaaii.com — ข้อมูล Google profile ที่ผูกอยู่จะถูกลบภายใน 30 วัน",
            <>ท่านสามารถเพิกถอนสิทธิ์การเข้าถึงของ FlowCloser ได้ตลอดเวลาผ่าน{" "}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: "var(--blue-700)" }}>
                myaccount.google.com/permissions
              </a>
              {" "}— การเพิกถอนจะป้องกัน sign-in ครั้งถัดไปทันที แต่ไม่ลบข้อมูลที่บันทึกไว้แล้วโดยอัตโนมัติ (ใช้ขั้นตอนลบบัญชีข้างต้นสำหรับการลบเต็มรูปแบบ)</>,
          ]}
        />

        <h3 className="text-[16px] font-semibold mt-4 mb-1">การแบ่งปัน (Sharing)</h3>
        <p>
          <b>ข้อมูล Google user data ไม่ถูกแบ่งปันให้บุคคลที่สาม</b> ยกเว้น:
        </p>
        <Bullets
          items={[
            "ผู้ให้บริการ infrastructure ที่จำเป็นในการดำเนินบริการ (เช่น Cloudflare สำหรับ DDoS protection, ผู้ให้บริการ database สำหรับจัดเก็บ) ซึ่งทุกรายผูกพันด้วยข้อตกลงรักษาความลับและ DPA",
            "กรณีที่กฎหมายไทย (เช่น PDPA, คำสั่งศาล) กำหนด",
          ]}
        />
        <p>
          <b>ข้อมูล Google user data ไม่เคยถูกขาย เช่า หรือใช้สำหรับ retargeting</b> ในรูปแบบใด ๆ
        </p>

        <h3 className="text-[16px] font-semibold mt-4 mb-1">การปฏิบัติตามนโยบาย Google</h3>
        <p>
          การใช้งานข้อมูลที่ได้รับจาก Google APIs ของ FlowCloser ปฏิบัติตาม{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            Google API Services User Data Policy
          </a>
          {" "}รวมถึงข้อกำหนด <b>Limited Use</b> ทุกประการ
        </p>
      </Section>
    </div>
  );
}

// ─── Privacy — English ──────────────────────────────────────
function PrivacyContentEN() {
  return (
    <div>
      <h2 className="text-[28px] font-bold mb-2 tracking-tight" style={{ color: "var(--ink)" }}>
        Privacy Policy
      </h2>
      <p className="text-[14px] mb-9" style={{ color: "var(--ink-3)" }}>
        Aligned with Thailand's Personal Data Protection Act B.E. 2562 (PDPA).
      </p>

      <Section num="01" title="Data Controller">
        <p>The platform operator and Data Controller responsible for your personal data is:</p>
        <div
          className="rounded-xl p-4 mt-2"
          style={{ background: "var(--bg-soft)", border: "1px solid var(--line)" }}
        >
          <p className="text-[14px]">
            <b>Super Firm Co., Ltd.</b>
            <br />
            (SUPER FIRM CO., LTD.)
            <br />
            Company registration no.: <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>0105565082136</span>
            <br />
            Address: 281/95 Vibhavadi Rangsit Road, Sanambin, Don Mueang, Bangkok 10210, Thailand
            <br />
            Data Protection Officer (DPO):{" "}
            <a
              href="mailto:success@seaaii.com"
              className="hover:underline"
              style={{ color: "var(--blue-700)" }}
            >
              success@seaaii.com
            </a>
          </p>
        </div>
      </Section>

      <Section num="02" title="Our Role and Scope">
        <p>FlowCloser is a SaaS platform that helps online merchants use AI to respond to customers and close sales. We act in two capacities:</p>
        <Bullets
          items={[
            <><b>Data Controller:</b> for the merchants who sign up and use the Service (account data, billing, system usage).</>,
            <><b>Data Processor:</b> for the messages and data of End Users that customers send to the merchant's connected page or account — we process this data on the merchant's behalf so the AI can reply. The data belongs to the merchant and the End Users, not to us.</>,
          ]}
        />
      </Section>

      <Section num="03" title="Data We Collect">
        <p>We collect the following categories of data:</p>
        <Bullets
          items={[
            <><b>Merchant account data:</b> name, email, hashed password (bcrypt), phone number, profile picture, and information from your Google Account if you choose Google sign-in.</>,
            <><b>Store data:</b> shop name and details, address, tax ID, contact phone, bank account for receiving payment, product listings, prices, product images, and promotions.</>,
            <><b>Usage data:</b> sign-in timestamps, AI configuration, order history within the system, AI token usage.</>,
            <><b>Billing data:</b> chosen subscription tier, payment history through Stripe (we do not store card numbers — Stripe does).</>,
            <><b>Technical data:</b> IP address, User-Agent, application logs used for security and to detect abnormal activity.</>,
            <><b>Messaging-platform data:</b> see Section 04.</>,
          ]}
        />
      </Section>

      <Section num="04" title="Data from Facebook Messenger and LINE Official Account">
        <p>
          When a merchant connects FlowCloser with a Facebook Page or LINE Official Account so our AI can reply to their customers,
          we receive and process the following data <b>on the merchant's behalf (as Data Processor)</b>:
        </p>
        <Bullets
          items={[
            <><b>Page / channel data:</b> Page ID, Page Access Token, Page Name (used to verify the right to reply on behalf of that page).</>,
            <><b>End-user identifiers:</b> Display Name, profile picture, Page-Scoped User ID (PSID for Facebook, userId for LINE).</>,
            <><b>Message content:</b> messages, images, and files that End Users send to the merchant's page or channel.</>,
            <><b>Order data:</b> shipping address, phone number, and other information End Users provide to the merchant in order to place an order.</>,
          ]}
        />
        <p>
          We use this data only to (a) let the AI respond and create orders on the merchant's behalf,
          (b) record conversation history so the merchant can review it in the dashboard, and (c) debug or fix technical issues when necessary.
          <b> We do not use End Users' messages for marketing, advertising, resale to third parties, or to train our AI models.</b>
        </p>
      </Section>

      <Section num="05" title="Purposes and Legal Bases">
        <Bullets
          items={[
            <><b>Service delivery (contract):</b> performing the FlowCloser service contract with the merchant.</>,
            <><b>Replying to End Users (contract / legitimate interests):</b> processing messages so the AI can respond on the merchant's instruction.</>,
            <><b>Security and fraud prevention (legitimate interests):</b> reviewing logs, rate limiting, payment-slip verification.</>,
            <><b>Service improvement (legitimate interests):</b> aggregate analytics that do not identify individuals.</>,
            <><b>Legal compliance:</b> for example, retaining tax invoices and payment records under Thai tax law.</>,
            <><b>Communications (consent):</b> sending product news and updates by email; you can opt out at any time.</>,
          ]}
        />
      </Section>

      <Section num="06" title="Subprocessors">
        <p>To deliver the Service we engage the following third-party subprocessors. All are bound by confidentiality and security commitments:</p>
        <Bullets
          items={[
            <><b>Google LLC / AIML API:</b> processes messages through the Gemini model to generate AI replies (data sent is not used for model training, per provider policy).</>,
            <><b>Meta Platforms, Inc.:</b> Facebook Messenger Platform — sending and receiving messages with merchant pages.</>,
            <><b>LINE Corporation:</b> LINE Messaging API — sending and receiving messages with merchant LINE Official Accounts.</>,
            <><b>Cloudflare R2:</b> object storage for product images and payment slips.</>,
            <><b>Stripe, Inc.:</b> processing of subscription payments (we do not see card numbers).</>,
            <><b>Supabase / MySQL provider:</b> primary database for shop, account, and order data (servers located in Thailand).</>,
            <><b>SMTP provider:</b> delivery of verification, password-reset, and system-notification emails.</>,
            <><b>EasySlip API:</b> verification of bank-transfer slips (only when the merchant enables it).</>,
          ]}
        />
      </Section>

      <Section num="07" title="Cross-Border Data Transfer">
        <p>
          The primary FlowCloser database is hosted in <b>Thailand</b>.
          However, processing through the AI provider (Google/AIML), Meta, LINE, Cloudflare, Stripe, and other providers
          may involve data being processed outside Thailand (e.g. the United States, Singapore, Japan).
          These providers comply with international standards such as GDPR, ISO/IEC 27001, and SOC 2,
          giving protections substantially comparable to Thailand's PDPA.
        </p>
      </Section>

      <Section num="08" title="Data Retention Periods">
        <Bullets
          items={[
            <><b>Merchant accounts:</b> for the duration of use plus 90 days after closure (to allow recovery).</>,
            <><b>Conversation history and orders:</b> 12 months from the most recent message, so the merchant can review past activity.</>,
            <><b>Payment slips and end-user uploads:</b> 90 days, after which they are deleted automatically.</>,
            <><b>System logs and IP records:</b> 30 days, for security and incident review.</>,
            <><b>Financial / tax records:</b> 5 years, as required by Thai law.</>,
            <><b>Password-reset and email-verification tokens:</b> 1 hour, then deleted.</>,
          ]}
        />
      </Section>

      <Section num="09" title="Security Measures">
        <Bullets
          items={[
            "Encryption in transit (HTTPS/TLS) and at rest for sensitive fields (passwords with bcrypt, tokens with SHA-256).",
            "Least-privilege access — only personnel who need access have it.",
            "Rate limiting and abuse prevention (account enumeration, brute force).",
            "Webhook signature verification for Facebook and LINE callbacks to reject forged data.",
            "Regular backups and periodic security reviews.",
            "Authentication via OAuth (Google) or hashed passwords.",
          ]}
        />
      </Section>

      <Section num="10" title="Your Rights as a Data Subject">
        <p>Under the PDPA, you have the right to:</p>
        <Bullets
          items={[
            <><b>Access:</b> request a copy of the personal data we hold about you.</>,
            <><b>Rectification:</b> request correction of inaccurate or incomplete data.</>,
            <><b>Erasure:</b> request deletion when the data is no longer needed or you have withdrawn consent.</>,
            <><b>Restriction:</b> request that we limit our use of the data in certain cases.</>,
            <><b>Object:</b> object to processing for certain purposes.</>,
            <><b>Data portability:</b> request the data in a machine-readable format and have it transferred to another provider.</>,
            <><b>Withdraw consent:</b> withdraw consent that you previously gave, at any time.</>,
            <><b>Lodge a complaint:</b> with the Office of the Personal Data Protection Committee (PDPC) of Thailand.</>,
          ]}
        />
        <p>
          To exercise any of these rights, email{" "}
          <a
            href="mailto:success@seaaii.com"
            className="hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            success@seaaii.com
          </a>{" "}
          or use the{" "}
          <Link href="/data-deletion" className="hover:underline" style={{ color: "var(--blue-700)" }}>
            data deletion
          </Link>{" "}
          form. We will respond within 30 days.
        </p>
      </Section>

      <Section num="11" title="Cookies and Tracking">
        <p>We use cookies to:</p>
        <Bullets
          items={[
            "Maintain your sign-in session (NextAuth cookie) — required to use the Service.",
            "Remember settings and the shop you currently work on.",
            "Prevent CSRF and protect the system from abuse.",
          ]}
        />
        <p>We do not use cookies for advertising or third-party tracking. You can manage cookies through your browser at any time.</p>
      </Section>

      <Section num="12" title="Minors">
        <p>
          FlowCloser is designed for business owners who are at least 20 years old.
          We do not knowingly collect personal data from anyone under 20; if we discover such data, we will delete it promptly.
        </p>
      </Section>

      <Section num="13" title="Data Breach Notification">
        <p>
          In the event of a personal-data breach that poses a risk to the rights and freedoms of individuals,
          we will notify the Personal Data Protection Committee (PDPC) of Thailand <b>within 72 hours</b>,
          and inform affected users by email or in-app notification as soon as reasonably possible.
        </p>
      </Section>

      <Section num="14" title="Changes to This Policy">
        <p>
          We may update this policy from time to time.
          Material changes will be announced at least 14 days in advance via email and an in-app banner.
          Continued use after the new version takes effect constitutes acceptance.
        </p>
      </Section>

      <Section num="15" title="Contact (DPO)">
        <p>For questions or to exercise PDPA rights, contact our Data Protection Officer:</p>
        <div
          className="rounded-xl p-4 mt-2"
          style={{ background: "var(--bg-soft)", border: "1px solid var(--line)" }}
        >
          <p className="text-[14px]">
            <b>Super Firm Co., Ltd.</b>
            <br />
            281/95 Vibhavadi Rangsit Road, Sanambin, Don Mueang, Bangkok 10210, Thailand
            <br />
            Email:{" "}
            <a
              href="mailto:success@seaaii.com"
              className="hover:underline"
              style={{ color: "var(--blue-700)" }}
            >
              success@seaaii.com
            </a>
            <br />
            or via our{" "}
            <Link href="/contact" className="hover:underline" style={{ color: "var(--blue-700)" }}>
              contact page
            </Link>.
          </p>
        </div>
      </Section>

      <Section num="16" title="Google Account Data (for Google OAuth Verification)">
        <p>
          When you choose &ldquo;Sign in with Google&rdquo;, FlowCloser uses Google OAuth 2.0
          with only the standard <code>openid</code>, <code>profile</code>, and <code>email</code>{" "}
          scopes. This section fully discloses how we access, use, store, and share Google user
          data in compliance with the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            Google API Services User Data Policy
          </a>.
        </p>

        <h3 className="text-[16px] font-semibold mt-4 mb-1">Data Accessed</h3>
        <p>Through the <code>openid profile email</code> scopes, we access ONLY the following non-sensitive profile fields:</p>
        <Bullets
          items={[
            "Email address — used as the primary identifier for your FlowCloser merchant account",
            "Display name — shown in the FlowCloser dashboard so team members can identify each other",
            "Google profile picture URL — used as your avatar in the dashboard",
            "Google account unique identifier (sub) — used to re-link future sign-ins to the same FlowCloser account",
            "Email verification status (email_verified) — used to decide whether an additional verification email is needed",
          ]}
        />
        <p>
          We DO NOT access, request, or store any of the following:
        </p>
        <Bullets
          items={[
            "Gmail (messages, labels, contacts within Gmail)",
            "Google Calendar (events, appointments)",
            "Google Drive, Docs, Sheets, or Slides (any file type)",
            "Google Contacts",
            "YouTube (history, subscriptions, likes)",
            "Google Photos, Google Fit, Google Pay, or Maps history",
            "Any sensitive or restricted Google API scopes",
          ]}
        />

        <h3 className="text-[16px] font-semibold mt-4 mb-1">Data Usage</h3>
        <p>The Google profile data described above is used solely for:</p>
        <Bullets
          items={[
            <><b>Authentication:</b> verifying your identity when you sign in to FlowCloser.</>,
            <><b>Account creation / linking:</b> creating your FlowCloser merchant account, or linking your Google sign-in to a pre-existing account that shares the same verified email address.</>,
            <><b>Display personalization:</b> showing your display name and profile picture in the FlowCloser dashboard so you and your team members can identify each other.</>,
          ]}
        />
        <p>
          We DO NOT use Google user data for:
        </p>
        <Bullets
          items={[
            "Advertising or marketing communications unrelated to the FlowCloser service",
            "Training machine learning or AI models — Google profile data is never sent to our AI provider; only the chat content your customers voluntarily type into your messaging channel is processed by AI",
            "Selling, renting, leasing, or any monetization to third parties",
            "Profile enrichment, cross-site tracking, or retargeting in any form",
          ]}
        />

        <h3 className="text-[16px] font-semibold mt-4 mb-1">Storage and Retention</h3>
        <Bullets
          items={[
            "Google profile data is stored on FlowCloser servers located in Thailand, encrypted in transit (TLS) and at rest (disk-level encryption).",
            "Retained while your FlowCloser account is active, plus 90 days after closure for recovery purposes.",
            "You may request account deletion by emailing success@seaaii.com — all linked Google profile data is deleted within 30 days.",
            <>You may revoke FlowCloser&rsquo;s access to your Google Account at any time via{" "}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: "var(--blue-700)" }}>
                myaccount.google.com/permissions
              </a>
              . Revocation immediately prevents future sign-ins but does not by itself delete previously-stored data — use the account-deletion flow above for full removal.</>,
          ]}
        />

        <h3 className="text-[16px] font-semibold mt-4 mb-1">Sharing</h3>
        <p>
          <b>Google user data is NEVER shared with third parties</b> except:
        </p>
        <Bullets
          items={[
            "Infrastructure subprocessors strictly required to operate the service (e.g. Cloudflare for DDoS protection, the database provider for storage), all bound by confidentiality agreements and data processing addenda.",
            "When required by applicable Thai law (e.g. PDPA, lawful court orders).",
          ]}
        />
        <p>
          <b>Google user data is never sold, rented, or used for retargeting in any form.</b>
        </p>

        <h3 className="text-[16px] font-semibold mt-4 mb-1">Google Policy Compliance</h3>
        <p>
          FlowCloser&rsquo;s use of information received from Google APIs adheres to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            Google API Services User Data Policy
          </a>
          , including the <b>Limited Use</b> requirements.
        </p>
      </Section>
    </div>
  );
}
